import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetTonTransactionListQuery } from '@/api/queries/user';
import {
  H2,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';

import { ModalContext } from '../Modal/config';

import { EarningsTable } from './EarningsTable/EarningsTable';
import { Background } from './Background';

import css from './styles.module.scss';

import usdPng from '/img/icons/usd.png';
import usdWebp from '/img/icons/usd.webp';

const WalletEarnDetails: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { closeModal } = useContext(ModalContext);

  const { data, isLoading } = useGetTonTransactionListQuery({ page });

  const countOfPages = Math.ceil((data?.totalCount || 1) / 10);

  const detailsGeneralData = [
    {
      id: '1',
      title: 'Total earned',
      value: data?.totalEarned,
    },

    {
      id: '2',
      title: 'Withdrawn',
      value: data?.withdrawn,
    },
  ];

  return (
    <div className="w-full relative">
      <Background />
      <Close
        onClick={closeModal}
        variant="modal"
        imageVariant="modal"
        className="absolute top-4 right-4"
      />
      <div className="w-full flex flex-col items-center relative z-[2] pt-[30px]">
        <H2 weight="bold" className="mb-[30px]">
          {t('Earnings details')}
        </H2>
        <div className="flex w-full gap-2 items-center px-4 mb-2">
          {detailsGeneralData.map(({ title, id, value }) => (
            <div
              key={id}
              className="flex-1 bg-white/10 rounded-lg h-[3.375rem] flex flex-col items-center justify-center gap-[2px]"
            >
              <StringXSmall className="!text-white/40 !text-[12px]">
                {t(title)}
              </StringXSmall>
              <div className="flex items-center gap-1">
                <Picture
                  className="w-6 h-6 shrink-0"
                  img={usdPng}
                  webp={usdWebp}
                  alt="toncoin"
                />
                <StringMain weight="bold" className="!leading-1.2">
                  {value || 0}
                </StringMain>
              </div>
            </div>
          ))}
        </div>
        <EarningsTable transactionList={data?.transactions} />
        <div className={css.showMoreWrapper}>
          <MainButton
            onClick={() => setPage((state) => state + 1)}
            isLoading={isLoading}
            inactive={page >= countOfPages}
            disabled={page >= countOfPages || isLoading}
            text={t('Show more')}
          />
        </div>
      </div>
    </div>
  );
};

export { WalletEarnDetails };
